import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { COLORS, MOTION_MS, SERIES, fmtCount, fmtDay, reducedMotion } from "./emsTheme";

const todayIst = () => {
    const now = new Date();
    const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
    return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}`;
};

const tooltipStyle = { fontSize: 12, borderRadius: 6, border: `1px solid ${COLORS.grid}` };
const axisTick = { fontSize: 12, fill: COLORS.ink3 };

/** One row per series, today marked as incomplete; ignores the split helper series. */
const LineTip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{ ...tooltipStyle, background: COLORS.surface, padding: "8px 10px" }}>
            <div style={{ fontWeight: 600, color: COLORS.ink, marginBottom: 4 }}>{d.label}{d.partial ? " (today so far)" : ""}</div>
            <div style={{ color: SERIES[0] }}>Raised: {fmtCount(d.created)}</div>
            <div style={{ color: COLORS.orangeText }}>Resolved: {fmtCount(d.resolved)}</div>
        </div>
    );
};

/**
 * Created vs resolved per day. 7 or more days: a line, with today (incomplete) dashed. Fewer: a
 * column chart, with today's columns faded. Both start at zero.
 * points: [{ date: "2026-10-13", created, resolved }]
 */
const TrendChart = ({ points, height = 260 }) => {
    const motion = !reducedMotion();
    const today = todayIst();
    const data = (points || []).map((p) => ({ ...p, label: fmtDay(p.date), partial: p.date === today }));
    const n = data.length;

    if (n < 7) {
        return (
            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
                    <CartesianGrid stroke={COLORS.grid} vertical={false} />
                    <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={{ stroke: COLORS.grid }} />
                    <YAxis tick={axisTick} tickLine={false} axisLine={false} allowDecimals={false} width={44} domain={[0, "auto"]} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [fmtCount(v), name]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="created" name="Raised" fill={SERIES[0]} isAnimationActive={motion} animationDuration={MOTION_MS} maxBarSize={36}>
                        {data.map((d) => <Cell key={`c-${d.date}`} fillOpacity={d.partial ? 0.45 : 1} />)}
                    </Bar>
                    <Bar dataKey="resolved" name="Resolved" fill={SERIES[1]} isAnimationActive={motion} animationDuration={MOTION_MS} maxBarSize={36}>
                        {data.map((d) => <Cell key={`r-${d.date}`} fillOpacity={d.partial ? 0.45 : 1} />)}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        );
    }

    // Split each series so the last segment (into today, if today is in range) can be dashed.
    const lastPartial = data[n - 1].partial;
    const shaped = data.map((d, i) => ({
        ...d,
        createdDone: lastPartial && i === n - 1 ? null : d.created,
        resolvedDone: lastPartial && i === n - 1 ? null : d.resolved,
        createdPart: lastPartial && i >= n - 2 ? d.created : null,
        resolvedPart: lastPartial && i >= n - 2 ? d.resolved : null,
    }));
    return (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={shaped} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={COLORS.grid} vertical={false} />
                <XAxis dataKey="label" tick={axisTick} tickLine={false} axisLine={{ stroke: COLORS.grid }} minTickGap={16} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} allowDecimals={false} width={44} domain={[0, "auto"]} />
                <Tooltip content={<LineTip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} payload={[
                    { value: "Raised", type: "line", color: SERIES[0] },
                    { value: "Resolved", type: "line", color: SERIES[1] },
                ]} />
                <Line dataKey="createdDone" name="Raised" stroke={SERIES[0]} strokeWidth={2} dot={false} isAnimationActive={motion} animationDuration={MOTION_MS} connectNulls={false} />
                <Line dataKey="resolvedDone" name="Resolved" stroke={SERIES[1]} strokeWidth={2} dot={false} isAnimationActive={motion} animationDuration={MOTION_MS} connectNulls={false} />
                <Line dataKey="createdPart" name="Raised (today so far)" stroke={SERIES[0]} strokeWidth={2} strokeDasharray="4 4" dot={false} isAnimationActive={false} legendType="none" />
                <Line dataKey="resolvedPart" name="Resolved (today so far)" stroke={SERIES[1]} strokeWidth={2} strokeDasharray="4 4" dot={false} isAnimationActive={false} legendType="none" />
            </LineChart>
        </ResponsiveContainer>
    );
};

export default TrendChart;
